/* Ergo Dream — global behaviour (header, drawers, search, cart, reveal).
   No dependencies. Everything talks to Shopify's own Ajax Cart API and
   Section Rendering API, so totals, discounts and prices always come from Shopify. */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const announce = (message) => {
    const region = $('[data-live-region]');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 60);
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  theme.utils = { $, $$, debounce, announce, parseHTML };

  /* ------------------------------------------------------------------
     Header: measure height, solid background after scrolling
     ------------------------------------------------------------------ */
  const initHeader = () => {
    const header = $('[data-header]');
    if (!header || header._ready) return;
    header._ready = true;
    const root = document.documentElement;

    const setHeight = () => root.style.setProperty('--header-height', `${header.offsetHeight}px`);
    setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(setHeight).observe(header);

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        header.classList.toggle('is-scrolled', window.scrollY > 24);
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  };

  /* ------------------------------------------------------------------
     Announcement bar rotation
     ------------------------------------------------------------------ */
  const initAnnouncements = () => {
    $$('[data-announcement]').forEach((bar) => {
      const slides = $$('.announcement__slide', bar);
      if (slides.length < 2 || bar._rotating) return;
      bar._rotating = true;
      let index = 0;
      let paused = false;
      const interval = Number(bar.dataset.interval) || 5000;
      bar.addEventListener('mouseenter', () => { paused = true; });
      bar.addEventListener('mouseleave', () => { paused = false; });
      bar.addEventListener('focusin', () => { paused = true; });
      bar.addEventListener('focusout', () => { paused = false; });
      window.setInterval(() => {
        if (paused || document.hidden) return;
        slides[index].classList.remove('is-active');
        index = (index + 1) % slides.length;
        slides[index].classList.add('is-active');
      }, interval);
    });
  };

  /* ------------------------------------------------------------------
     Drawers (native <dialog>: focus trap + Esc come for free)
     ------------------------------------------------------------------ */
  const Drawer = {
    open(dialog, opener) {
      if (!dialog || dialog.open) return;
      dialog._opener = opener || document.activeElement;
      dialog.showModal();
      document.documentElement.classList.add('scroll-locked');
      $$(`[aria-controls="${dialog.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
      window.requestAnimationFrame(() => dialog.classList.add('is-open'));
    },
    close(dialog) {
      if (!dialog || !dialog.open) return;
      dialog.classList.remove('is-open');
      $$(`[aria-controls="${dialog.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
      const finish = () => {
        dialog.close();
        document.documentElement.classList.remove('scroll-locked');
        if (dialog._opener && document.contains(dialog._opener)) dialog._opener.focus({ preventScroll: true });
      };
      if (reducedMotion.matches) finish();
      else window.setTimeout(finish, 380);
    },
  };
  theme.Drawer = Drawer;

  const initDrawers = () => {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-drawer-open]');
      if (opener) {
        const dialog = document.getElementById(opener.dataset.drawerOpen);
        if (dialog) {
          event.preventDefault();
          Drawer.open(dialog, opener);
        }
        return;
      }
      const closer = event.target.closest('[data-drawer-close]');
      if (closer) {
        Drawer.close(closer.closest('dialog'));
        return;
      }
      // Click on the backdrop (the dialog element itself, outside its content)
      if (event.target.matches('dialog[data-drawer]')) Drawer.close(event.target);
    });

    $$('dialog[data-drawer]').forEach((dialog) => {
      dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        Drawer.close(dialog);
      });
    });
  };

  /* ------------------------------------------------------------------
     <details> dropdowns: close on outside click / Esc
     ------------------------------------------------------------------ */
  const initDetails = () => {
    document.addEventListener('click', (event) => {
      $$('details[data-auto-close][open]').forEach((details) => {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      $$('details[data-auto-close][open]').forEach((details) => {
        details.removeAttribute('open');
        details.querySelector('summary')?.focus();
      });
    });
    document.addEventListener('focusout', (event) => {
      const details = event.target.closest('details[data-auto-close][open]');
      if (details && event.relatedTarget && !details.contains(event.relatedTarget)) details.removeAttribute('open');
    });
  };

  /* ------------------------------------------------------------------
     Search modal + predictive search
     ------------------------------------------------------------------ */
  const initSearch = () => {
    const modal = $('[data-search-modal]');
    if (!modal) return;
    const input = $('[data-predictive-search-input]', modal);
    const results = $('[data-predictive-search-results]', modal);
    let controller;

    const open = (opener) => {
      modal._opener = opener;
      modal.showModal();
      document.documentElement.classList.add('scroll-locked');
      window.requestAnimationFrame(() => modal.classList.add('is-open'));
      input?.focus();
    };
    const close = () => {
      modal.classList.remove('is-open');
      const finish = () => {
        modal.close();
        document.documentElement.classList.remove('scroll-locked');
        modal._opener?.focus({ preventScroll: true });
      };
      if (reducedMotion.matches) finish();
      else window.setTimeout(finish, 300);
    };

    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-search-open]');
      if (opener) {
        event.preventDefault();
        open(opener);
        return;
      }
      if (event.target.closest('[data-search-close]') || event.target === modal) close();
    });
    modal.addEventListener('cancel', (event) => {
      event.preventDefault();
      close();
    });

    if (!input || !results || !routes.predictiveSearch) return;

    const render = async (term) => {
      if (term.length < 2) {
        results.innerHTML = '';
        return;
      }
      controller?.abort();
      controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[limit]': '6',
        'resources[type]': 'product,collection,page,article,query',
        'resources[options][unavailable_products]': 'last',
      });
      try {
        const response = await fetch(`${routes.predictiveSearch}?${params}`, { signal: controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = parseHTML(await response.text());
        const content = $('[data-predictive-search-content]', html);
        results.innerHTML = content ? content.outerHTML : '';
      } catch (error) {
        if (error.name !== 'AbortError') results.innerHTML = '';
      }
    };

    input.addEventListener('input', debounce(() => render(input.value.trim()), 250));

    // Arrow keys move between results; Enter follows the link natively.
    modal.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const links = $$('a', results);
      if (!links.length) return;
      event.preventDefault();
      const current = links.indexOf(document.activeElement);
      let next = event.key === 'ArrowDown' ? current + 1 : current - 1;
      if (next < -1) next = links.length - 1;
      if (next === -1 || next >= links.length) {
        input.focus();
        return;
      }
      links[next].focus();
    });
  };

  /* ------------------------------------------------------------------
     Quantity steppers (product forms + cart)
     ------------------------------------------------------------------ */
  const initQuantity = () => {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-minus], [data-quantity-plus]');
      if (!button) return;
      const wrapper = button.closest('[data-quantity]');
      const input = wrapper && $('input', wrapper);
      if (!input) return;
      const step = Number(input.step) || 1;
      const min = input.hasAttribute('data-cart-quantity') ? 0 : Number(input.min) || 1;
      const max = input.max ? Number(input.max) : Infinity;
      const current = Number(input.value) || 0;
      let next = button.hasAttribute('data-quantity-plus') ? current + step : current - step;
      if (input.hasAttribute('data-cart-quantity') && next < Number(input.dataset.min || 1)) next = 0;
      next = Math.min(Math.max(next, min), max);
      if (next === current) return;
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    document.addEventListener('change', (event) => {
      const input = event.target.closest('[data-quantity] input');
      if (!input) return;
      const wrapper = input.closest('[data-quantity]');
      const max = input.max ? Number(input.max) : Infinity;
      const value = Number(input.value);
      const plus = $('[data-quantity-plus]', wrapper);
      const minus = $('[data-quantity-minus]', wrapper);
      if (plus) plus.disabled = value >= max;
      if (minus && !input.hasAttribute('data-cart-quantity')) minus.disabled = value <= (Number(input.min) || 1);
    });
  };

  /* ------------------------------------------------------------------
     Cart
     ------------------------------------------------------------------ */
  const Cart = {
    sectionIds() {
      const ids = new Set(['cart-drawer']);
      $$('[data-cart-section]').forEach((el) => ids.add(el.dataset.sectionId));
      return Array.from(ids);
    },

    updateCount(count) {
      if (typeof count !== 'number' || Number.isNaN(count)) return;
      $$('[data-cart-count]').forEach((el) => {
        el.dataset.count = count;
        el.textContent = count > 0 ? (count > 99 ? '99+' : count) : '';
      });
    },

    renderSections(sections) {
      if (!sections) return;
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-drawer') {
          const next = $('[data-cart-drawer-content]', doc);
          const current = $('[data-cart-drawer-content]');
          if (next && current) {
            current.replaceWith(next);
            this.updateCount(Number(next.dataset.cartCountSource));
          }
          return;
        }
        const next = $('[data-cart-section-content]', doc);
        const current = $(`[data-cart-section][data-section-id="${id}"] [data-cart-section-content]`);
        if (next && current) current.replaceWith(next);
      });
      document.dispatchEvent(new CustomEvent('cart:updated'));
    },

    async add(form, submitter) {
      const buttons = $$('[data-add-button]', form);
      if (submitter && !buttons.includes(submitter)) buttons.push(submitter);
      if (form.id) $$(`button[form="${form.id}"][data-add-button]`).forEach((b) => !buttons.includes(b) && buttons.push(b));
      const errorBox = $('[data-form-error]', form.closest('[data-product-root]') || form);

      buttons.forEach((button) => {
        button.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-disabled', 'true');
      });
      if (errorBox) errorBox.hidden = true;

      const formData = new FormData(form);
      const useDrawer = theme.cartType !== 'page' && $('[data-cart-drawer]');
      if (useDrawer) {
        formData.append('sections', this.sectionIds().join(','));
        formData.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) {
          throw new Error(data.description || data.message || strings.error);
        }
        if (!useDrawer) {
          window.location.href = routes.cart;
          return;
        }
        this.renderSections(data.sections);
        announce(strings.added);
        Drawer.open($('[data-cart-drawer]'), submitter || buttons[0]);
      } catch (error) {
        if (errorBox) {
          $('[data-form-error-message]', errorBox).textContent = error.message || strings.error;
          errorBox.hidden = false;
        } else {
          announce(error.message || strings.error);
        }
      } finally {
        buttons.forEach((button) => {
          button.removeAttribute('aria-busy');
          if (!button.disabled) button.removeAttribute('aria-disabled');
        });
      }
    },

    async change(key, quantity, itemEl) {
      const hadFocus = Boolean(itemEl && itemEl.contains(document.activeElement));
      itemEl?.classList.add('is-loading');
      const errorEl = itemEl && $('[data-cart-item-error]', itemEl);
      if (errorEl) errorEl.hidden = true;
      try {
        const response = await fetch(`${routes.cartChange}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            id: key,
            quantity,
            sections: this.sectionIds(),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || strings.error);
        this.renderSections(data.sections);
        this.updateCount(data.item_count);
        announce(strings.cartUpdated);
        if (hadFocus) {
          // Keep keyboard focus near where the user was.
          const scope = itemEl.closest('dialog') ? $('[data-cart-drawer]') : document;
          const target =
            $(`[data-cart-quantity][data-key="${CSS.escape(key)}"]`, scope) || $('[data-cart-focus-target]', scope);
          target?.focus({ preventScroll: true });
        }
      } catch (error) {
        itemEl?.classList.remove('is-loading');
        if (errorEl) {
          errorEl.textContent = error.message;
          errorEl.hidden = false;
        }
        // Re-sync the UI with the real cart.
        this.refresh();
      }
    },

    async refresh() {
      const ids = this.sectionIds();
      try {
        const response = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`);
        if (response.ok) this.renderSections(await response.json());
      } catch (_) { /* noop */ }
    },

    async updateNote(note) {
      try {
        await fetch(`${routes.cartUpdate}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note }),
        });
      } catch (_) { /* note still saved on checkout submit */ }
    },
  };
  theme.Cart = Cart;

  const initCart = () => {
    document.addEventListener('submit', (event) => {
      const form = event.target.closest('form[data-product-form]');
      if (!form) return;
      event.preventDefault();
      Cart.add(form, event.submitter);
    });

    const pending = new Map();
    document.addEventListener('change', (event) => {
      const input = event.target.closest('[data-cart-quantity]');
      if (input) {
        const key = input.dataset.key;
        clearTimeout(pending.get(key));
        pending.set(
          key,
          setTimeout(() => {
            const quantity = Math.max(0, parseInt(input.value, 10) || 0);
            Cart.change(key, quantity, input.closest('[data-cart-item]'));
          }, 350)
        );
        return;
      }
      const note = event.target.closest('[data-cart-note]');
      if (note) Cart.updateNote(note.value);
    });

    document.addEventListener('click', (event) => {
      const remove = event.target.closest('[data-cart-remove]');
      if (!remove) return;
      event.preventDefault();
      Cart.change(remove.dataset.key, 0, remove.closest('[data-cart-item]'));
    });
  };

  /* ------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------ */
  const initReveal = () => {
    const items = $$('[data-reveal]');
    if (!items.length) return;
    if (reducedMotion.matches || !('IntersectionObserver' in window) || !document.body.classList.contains('has-reveal')) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
    );
    items.forEach((el) => observer.observe(el));
    theme.revealObserver = observer;
  };

  /* ------------------------------------------------------------------
     Misc
     ------------------------------------------------------------------ */
  const initAutoSubmit = () => {
    document.addEventListener('change', (event) => {
      const select = event.target.closest('[data-auto-submit]');
      if (select && select.form) select.form.submit();
    });
  };

  const init = () => {
    initHeader();
    initAnnouncements();
    initDrawers();
    initDetails();
    initSearch();
    initQuantity();
    initCart();
    initReveal();
    initAutoSubmit();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run reveal + header logic when sections are re-rendered.
  document.addEventListener('shopify:section:load', () => {
    $$('[data-reveal]').forEach((el) => el.classList.add('is-revealed'));
    initAnnouncements();
    initHeader();
  });
})();
